import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TemporaryChatContext } from '../Context/temporaryChatContext';
import chatService from '../services/chatService';
import sessionsService from '../services/sessionsServices';
import { processChatStream } from '../utils/processChatStream';

export function useChatSubmit({ createSession = false, sessionId } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    updateUserChat,
    webSearch,
    updateBotChat,
    setChatmessages,
    setSessionID,
    updateChatSource,
    selectedAgent,
    toggleRef,
    fakeAiLoader,
    updateStepsResultsChat,
    updateAllStepsLoading,
    finishStream,
    fakeAiLoaderClear,
    updateChatLoaderMessage,
    updateStepsChat,
    updateToolImages,
    updateDelegateBotChat,
    updateThinkingStepsLoading,
  } = useContext(TemporaryChatContext);

  const mutationCreateSession = useMutation({
    mutationFn: () =>
      sessionsService.createSession({
        platform_id: 'gramielabs',
        agent_id: selectedAgent?.value,
        project_id: 'test',
        mode: selectedAgent?.mode,
      }),
  });

  const streamHandlers = {
    updateDelegateBotChat,
    updateThinkingStepsLoading,
    updateChatLoaderMessage,
    updateBotChat,
    updateStepsChat,
    updateStepsResultsChat,
    updateToolImages,
    updateChatSource,
    updateAllStepsLoading,
    finishStream,
  };

  const submitMessage = async text => {
    const query = text?.trim();
    if (!query || isSubmitting) return false;

    if (!createSession && !sessionId) {
      return false;
    }

    setIsSubmitting(true);
    toggleRef(false);

    try {
      if (createSession) {
        setChatmessages([]);
        const sessionRes = await mutationCreateSession.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['sessions'] });

        setSessionID(sessionRes?.session_id);
        updateUserChat(query);
        navigate(`/chat/${sessionRes?.session_id}`);

        fakeAiLoader();
        chatService.generateTitle({
          session_id: sessionRes?.session_id,
          query,
        });

        const aiResp = await chatService.createChat({
          platform_id: 'gramielabs',
          agent_id: selectedAgent?.value,
          session_id: sessionRes?.session_id,
          mode: selectedAgent?.mode,
          query,
          use_google_search: webSearch,
        });

        await processChatStream(aiResp.body.getReader(), streamHandlers);
      } else {
        updateUserChat(query);
        fakeAiLoader();

        const aiResp = await chatService.createChat({
          platform_id: 'gramielabs',
          agent_id: selectedAgent?.value,
          session_id: sessionId,
          query,
          mode: selectedAgent?.mode,
          use_google_search: webSearch,
        });

        await processChatStream(aiResp.body.getReader(), streamHandlers, { botChatSeparator: '\n \n' });
      }

      return true;
    } catch (error) {
      console.log(error);
      fakeAiLoaderClear();
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitMessage, isSubmitting };
}
